import React, { useState, useEffect } from 'react';
import { makeRequest } from '../../../utils/makeRequest';
import { pmRequestPageURL, pmApproveRequestURL, pmDenyRequestURL } from '../../../utils/urls';
import { toastify } from '../../../utils/toast';

const PMQuoteApproval = () => {
    const [requestData, setRequestData] = useState(null);
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSubmitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(null); // 'approved' | 'rejected'

    const token = new URLSearchParams(window.location.search).get('token');

    useEffect(() => {
        if (!token) {
            setError('Invalid or missing link. Please use the link sent to you via SMS or email.');
            setPageLoading(false);
            return;
        }

        const fetchRequestData = async () => {
            try {
                const response = await makeRequest(`${pmRequestPageURL}?token=${token}`);
                console.log("PM request response", response);
                if (response.success) {
                    setRequestData(response.data.data); // ← nested data
                } else {
                    setError(response.message || 'Failed to load request details.');
                }
            } catch (err) {
                setError('Network error. Please check your connection and try again.');
            } finally {
                setPageLoading(false);
            }
        };

        fetchRequestData();
    }, [token]);

    const handleApprove = async () => {
        setSubmitting(true);
        setLoading(true);
        try {
            const response = await makeRequest(pmApproveRequestURL, 'POST', { token });
            if (response.success) {
                setSubmitted('approved');
                toastify('Quote approved. Tenant has been notified.', 'success');
            } else {
                toastify(response.message, 'error');
            }
        } catch (err) {
            toastify(err.message, 'error');
        } finally {
            setSubmitting(false);
            setLoading(false);
        }
    };

    const handleReject = async () => {
        setLoading(true);
        try {
            const response = await makeRequest(pmDenyRequestURL, 'POST', { token, reason });
            if (response.success) {
                setSubmitted('rejected');
            } else {
                toastify(response.message, 'error');
            }
        } catch (err) {
            toastify(err.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    // ─── Loading State ────────────────────────────────────────────────────────
    if (pageLoading) {
        return (
            <div className="container py-5 text-center">
                <div className="spinner-border text-primary" role="status" />
                <p className="mt-3 text-muted">Loading service request...</p>
            </div>
        );
    }

    // ─── Error State ──────────────────────────────────────────────────────────
    if (error) {
        return (
            <div className="container py-5">
                <div className="alert alert-danger text-center">
                    <i className="ti ti-alert-circle fs-2 mb-2 d-block" />
                    <strong>Unable to load request</strong>
                    <p className="mb-0 mt-1">{error}</p>
                </div>
            </div>
        );
    }

    // ─── Submitted State ──────────────────────────────────────────────────────
    if (submitted) {
        return (
            <div className="container py-5">
                <div className={`alert ${submitted === 'approved' ? 'alert-success' : 'alert-warning'} text-center`}>
                    <i className={`ti ${submitted === 'approved' ? 'ti-circle-check' : 'ti-circle-x'} fs-2 mb-2 d-block`} />
                    <strong>
                        {submitted === 'approved' ? 'Quote approved!' : 'Quote denied'}
                    </strong>
                    <p className="mb-0 mt-1">
                        {submitted === 'approved'
                            ? 'The tenant has been notified and can now review and respond to the quote.'
                            : 'The request has been reset to Pending. The facility admin has been notified.'}
                    </p>
                </div>
            </div>
        );
    }

    // ─── Main View ────────────────────────────────────────────────────────────
    const { serviceRequest, vendor } = requestData;

    const formattedDate = serviceRequest.date
        ? new Date(serviceRequest.date).toLocaleDateString('en-KE', {
            day: 'numeric', month: 'long', year: 'numeric'
        })
        : '—';

    return (
        <div className="container py-4" style={{ maxWidth: 600 }}>
            <div className="card shadow-sm border-0">

                {/* Header */}
                <div className="card-header">
                    <h5 className="mb-0">Service request approval</h5>
                    <small className="opacity-75">Vendor: {vendor?.name ?? '—'}</small>
                </div>

                <div className="card-body">

                    {/* Request Details */}
                    <div className="mb-4">
                        <h6 className="text-muted">Request details for:</h6>
                        <h6>{serviceRequest.service?.serviceName}</h6>
                        <div className="bg-light p-3 rounded">
                            <p className="mb-2"><strong>Unit:</strong> {serviceRequest.unit?.name ?? '—'}</p>
                            <p className="mb-2"><strong>Request no.:</strong> {serviceRequest.requestNumber}</p>
                            <p className="mb-2"><strong>Description:</strong> {serviceRequest.description}</p>
                            <p className="mb-2"><strong>Preferred date:</strong> {formattedDate}</p>
                            <p className="mb-2"><strong>Preferred time:</strong> {serviceRequest.time || '—'}</p>
                            <p className="mb-0">
                                <strong>Status:</strong>
                                <span className="badge bg-warning ms-2">{serviceRequest.status}</span>
                            </p>
                        </div>
                    </div>

                    {/* Tenant Info */}
                    <div className="mb-4">
                        <h6 className="text-muted">Tenant info:</h6>
                        <div className="bg-light p-3 rounded">
                            <p className="mb-2"><strong>Name:</strong> {serviceRequest.customer?.fullName ?? '—'}</p>
                            <p className="mb-2"><strong>Phone:</strong> {serviceRequest.customer?.phoneNumber ?? '—'}</p>
                            <p className="mb-0"><strong>Email:</strong> {serviceRequest.customer?.email ?? '—'}</p>
                        </div>
                    </div>

                    {/* Vendor Info */}
                    {vendor && (
                        <div className="mb-4">
                            <h6 className="text-muted">Assigned vendor:</h6>
                            <div className="bg-light p-3 rounded">
                                <p className="mb-2"><strong>Name:</strong> {vendor.name}</p>
                                {vendor.contactDetails?.phone && (
                                    <p className="mb-2"><strong>Phone:</strong> {vendor.contactDetails.phone}</p>
                                )}
                                {vendor.contactDetails?.email && (
                                    <p className="mb-0"><strong>Email:</strong> {vendor.contactDetails.email}</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Vendor Quote */}
                    <div className="mb-4">
                        <h6 className="text-muted">Vendor quote:</h6>
                        <div className="bg-light p-3 rounded">
                            <p className="mb-2">
                                <strong>Amount:</strong> KES {Number(serviceRequest.quote).toLocaleString()}
                            </p>
                            {serviceRequest.notes && (
                                <p className="mb-0">
                                    <strong>Vendor notes:</strong> {serviceRequest.notes}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Denial Reason */}
                    <div className="mb-4">
                        <label className="form-label">
                            Denial reason <span className="text-muted small">(optional)</span>
                        </label>
                        <textarea
                            className="form-control"
                            rows="3"
                            placeholder="Add a reason if denying this quote..."
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            disabled={loading}
                        />
                    </div>

                    {/* Actions */}
                    <div className="d-flex gap-3">
                        <button
                            className="btn btn-success btn-lg w-100"
                            onClick={handleApprove}
                            disabled={loading}
                        >
                            {loading ? (
                                <span className="spinner-border spinner-border-sm me-2" />
                            ) : (
                                <i className="ti ti-check me-2" />
                            )}
                            {isSubmitting ? 'Submitting...' : 'Approve & notify tenant'}
                        </button>

                        <button
                            className="btn btn-outline-danger btn-lg w-100"
                            onClick={handleReject}
                            disabled={loading}
                        >
                            {loading ? (
                                <span className="spinner-border spinner-border-sm me-2" />
                            ) : (
                                <i className="ti ti-x me-2" />
                            )}
                            Deny quote
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default PMQuoteApproval;